import React from 'react';
import { Box, Typography, Grid2, Card, Chip } from '@mui/material';
import { ProjectDetailComponentProps, SiteCardProps, SiteDetailCardProps } from 'src/interface';
import CarbonCreditIcon from '@mui/icons-material/Cloud';
import BioCreditIcon from '@mui/icons-material/Nature';
import BioMonitorIcon from '@mui/icons-material/Monitor';

// Import SDG images that are selectable in ProjectDialog and potentially displayed here
import SDG1_img from 'src/resources/images/SDG1.png';
import SDG2_img from 'src/resources/images/SDG2.png';
// Based on ProjectDialog.tsx, the following SDGs are associated with specific images:
import SDG5_img from 'src/resources/images/SDG5.png'; // Used for 'SDG5' identifier
import SDG8_img from 'src/resources/images/SDG8.png'; // Used for 'SDG8' identifier
import SDG13_img from 'src/resources/images/SDG13.png'; // Used for 'SDG13' identifier
import SDG14_img from 'src/resources/images/SDG14.png'; // Used for 'SDG14' identifier
import SDG15_img from 'src/resources/images/SDG15.png'; // Used for 'SDG15' identifier
export const ProjectDetailComponent: React.FC<ProjectDetailComponentProps> = ({ id, title,
  subTitle,
  startDateTitle,
  startDate,
  endDateTitle,
  endDate,
  detailTitle,
  details,
  mapTitle,
  children,
  icon,
  projectType,
  sdg,
  creditPeriod,
  totalReduction,
  totalArea,
  areaUnit,
  hasCarbonModule,
  hasBiodiversityModule,
  hasEnvModule,
  country
}) => {
  // Map SDG identifiers (e.g., "SDG1", "SDG5") to imported image variables.
  // This map should cover all SDG identifiers that can be present in the `sdg` prop.
  // The keys are the identifiers (like 'SDG1') and values are the imported image modules.
  const sdgImageMap: Record<string, string> = {
    'SDG1': SDG1_img,
    'SDG2': SDG2_img,
    'SDG5': SDG5_img,
    'SDG8': SDG8_img,
    'SDG13': SDG13_img,
    'SDG14': SDG14_img,
    'SDG15': SDG15_img,
    // If other SDGs like SDG3, SDG4, SDG6, SDG7, SDG9-SDG12, SDG16, SDG17 can be selected
    // and have corresponding images (e.g., SDG3.png), add their imports and mappings here.
  };
  return (
    <Box>
      <Box my={3} display='flex' justifyContent='space-between'>
        <Box>
          <Typography sx={{ fontSize: '32px', fontWeight: 600 }}>{title}</Typography>
          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#ffb400' }}>{subTitle}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {icon && typeof icon === 'string' && (
                <Box sx={{ ml: 1 }}>
                  <img
                    src={icon}
                    alt="icon"
                    style={{ width: '20px', height: '25px', objectFit: 'contain' }}
                  />
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
            {hasCarbonModule && <Chip variant='outlined' icon={<CarbonCreditIcon />} label="Carbon" color={'info'} />}
            {hasBiodiversityModule && <Chip variant='outlined' icon={<BioCreditIcon />} label="Biodiversity" color={'info'} />}
            {hasEnvModule && <Chip variant='outlined' icon={<BioMonitorIcon />} label="Climate" color={'info'} />}
          </Box>
        </Box>
        <Box>
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 2, mb: 2, flexWrap: 'wrap', maxWidth: '380px' }}>
              {sdg?.map((sdgIdentifier: string, index: number) => {
                const imageSrc = sdgImageMap[sdgIdentifier];
                if (!imageSrc) {
                  // Log a warning if an image is missing for a given SDG identifier
                  console.warn(`Image not found for SDG: ${sdgIdentifier}. Ensure it's imported and mapped.`);
                  return null; // Or render a placeholder
                }
                return (
                  <img
                    key={index}
                    src={imageSrc} // Use the imported image variable
                    alt={`SDG ${sdgIdentifier.replace('SDG', '')}`} // e.g., "SDG 1", "SDG 5"
                    style={{
                      width: '60px',
                      height: '60px',
                      objectFit: 'contain',
                      borderRadius: '4px',
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Grid2 container rowSpacing={2} columnSpacing={{ xs: 2, sm: 3, md: 2 }}>
          {projectType &&
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '16px' }}>
                {'Project Type'}
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: '20px' }}>
                {projectType}
              </Typography>
            </Grid2>
          }

          {totalReduction &&
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '16px' }}>
                {'Total Emission Reduction'}
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: '20px' }}>
                {totalReduction}
              </Typography>
            </Grid2>
          }
          {/* {creditPeriod &&
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '16px' }}>
                {'Credit Period'}
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: '20px' }}>
                {creditPeriod}
              </Typography>
            </Grid2>
          }
          {startDate &&
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '16px' }}>
                {startDateTitle}
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: '20px' }}>
                {startDate}
              </Typography>
            </Grid2>
          }
          {endDate &&
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '16px' }}>
                {endDateTitle}
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: '20px' }}>
                {endDate}
              </Typography>
            </Grid2>
          } */}
          {detailTitle && <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '16px' }}>
              {'Project Developer'}
            </Typography>
            <Typography sx={{ fontWeight: 500, fontSize: '20px' }}>
              {detailTitle}
            </Typography>
          </Grid2>
          }
          {country && <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '16px' }}>
              {'Country'}
            </Typography>
            <Typography sx={{ fontWeight: 500, fontSize: '20px' }}>
              {country.label}
            </Typography>
          </Grid2>
          }

          {!!totalArea &&
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography sx={{ color: '#666666', fontWeight: 500, fontSize: '16px' }}>
                {'Total Area'}
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: '20px' }}>
                {Number(totalArea).toFixed(2)} {areaUnit || 'ha'}
              </Typography>
            </Grid2>
          }
        </Grid2>
      </Box>
      {details &&
        <Box my={3}>
          <Typography sx={{ fontSize: '17px', fontWeight: 500, color: '#666666', textAlign: 'justify' }}>{details}</Typography>
        </Box>
      }
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: '22px', fontWeight: 600, color: '#151E15' }}>{mapTitle}</Typography>
        {children}
      </Box>
    </Box>
  );
};

export const SiteDetailCard: React.FC<SiteDetailCardProps> = ({ siteTitle, siteDetail }) => {
  return (
    <Box display='flex' alignItems='center' gap={2}>
      <Typography sx={{ color: '#151E15', fontWeight: 600, fontSize: '24px' }}>{siteTitle}</Typography>
      <Typography sx={{ color: '#666666', fontWeight: 400, fontSize: '16px' }}>{siteDetail}</Typography>
    </Box>
  );
};

export const SiteCard: React.FC<SiteCardProps> = ({ title, amount, icon }) => {
  return (
    <Card sx={{
      display: 'flex',
      alignItems: 'center',
      padding: 2,
      borderRadius: 2,
      boxShadow: 1,
      backgroundColor: '#fff',
      '&:hover': {
        boxShadow: 3,
      },
      maxHeight: '500px'

    }}>
      {/* Icon section */}
      <Box sx={{ marginRight: 2, display: 'flex', alignItems: 'center' }}>
        {icon && (
          <img src={icon} alt={`${title} Icon`} width={50} height={50} />
        )}
      </Box>

      {/* Title and Amount Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontSize: '18px',
            fontWeight: 700,
            color: '#666666',
            textAlign: 'left',
            maxHeight: '180px'
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontSize: '17px',
            fontWeight: 600,
            color: '#151E15',
            textAlign: 'left',
            marginBottom: 1,
            maxHeight: '180px'
          }}
        >
          {amount}
        </Typography>
      </Box>
    </Card>
  );
};